import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

// ============================================================================
// Types
// ============================================================================

interface Notification {
    open: boolean;
    message: string;
    severity: AlertColor;
}

interface NotificationContextType {
    notify: (message: string, severity?: AlertColor) => void;
    notifySuccess: (message: string) => void;
    notifyError: (message: string) => void;
    notifyWarning: (message: string) => void;
    notifyInfo: (message: string) => void;
}

// ============================================================================
// Context
// ============================================================================

const NotificationContext = createContext<NotificationContextType | null>(null);

// ============================================================================
// Provider
// ============================================================================

/**
 * Global notification provider that replaces all window.alert() and alert() usage.
 * Wrap your app (or a section of it) with this provider, then use the
 * `useNotification()` hook to show Material UI Snackbar toasts.
 *
 * Usage:
 *   const { notifySuccess, notifyError } = useNotification();
 *   notifySuccess('Funnel created!');
 *   notifyError('Something went wrong');
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notification, setNotification] = useState<Notification>({
        open: false,
        message: '',
        severity: 'info',
    });

    const notify = useCallback((message: string, severity: AlertColor = 'info') => {
        setNotification({ open: true, message, severity });
    }, []);

    const notifySuccess = useCallback((message: string) => notify(message, 'success'), [notify]);
    const notifyError = useCallback((message: string) => notify(message, 'error'), [notify]);
    const notifyWarning = useCallback((message: string) => notify(message, 'warning'), [notify]);
    const notifyInfo = useCallback((message: string) => notify(message, 'info'), [notify]);

    const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setNotification(prev => ({ ...prev, open: false }));
    }, []);

    return (
        <NotificationContext.Provider value={{ notify, notifySuccess, notifyError, notifyWarning, notifyInfo }}>
            {children}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleClose}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%', fontWeight: 500 }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the global notification system.
 * Must be used inside a <NotificationProvider>.
 */
export const useNotification = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
